import classNames from 'classnames'
import {Link} from 'gatsby'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import * as React from 'react'

import {IconSearch} from '@/assets/IconSearch'
import {IS_DEVELOPMENT, RESOLVE_RELATIONS} from '@/common/config'
import {makeEditable} from '@/common/utils'
import DynamicComponent from '@/components/DynamicComponent'
import {Grid} from '@/components/Grid'
import {storyblokApi} from '@/libs/storyblok'

import Card from './Card'

const PagesList = ({
  backgroundColors,
  sectionBackground,
  cardsBackgroundColor,
  cardsButtonLabel,
  showMoreButtonLabel,
  categories,
  authors,
  showFilters,
  maxItems,
  components,
  searchPlaceholderLabel,
  _editable,
  pagination,
}) => {
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState()
  const [filters, setFilters] = React.useState([])
  const [stories, setStories] = React.useState([])
  const [allStories, setAllStories] = React.useState([])
  const [searchTerm, setSearchTerm] = React.useState('')
  const [isSearching, setIsSearching] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [storyLoaded, setStoryLoaded] = React.useState(false)
  const initialFilterDone = React.useRef(false)
  const currentSearchTerm = React.useRef(searchTerm)

  React.useEffect(() => {
    currentSearchTerm.current = searchTerm
  }, [searchTerm])

  function resetState() {
    setPage(1)
    setTotal(undefined)
  }

  const searchInStories = (value) => {
    setIsSearching(true)

    if (!value) {
      setStories(allStories)
      setIsSearching(false)
      return
    }

    const filteredStories = allStories.filter((story) =>
      story.name.toLowerCase().includes(value.toLowerCase())
    )
    setStories(filteredStories)
  }

  React.useEffect(() => {
    if (isSearching === false) return
    setIsLoading(true)

    const fetchStories = async () => {
      try {
        const {data} = await storyblokApi.get('cdn/stories/', {
          version:
            window.location.pathname.includes('storyblok-preview') || IS_DEVELOPMENT
              ? 'draft'
              : 'published',
          resolve_relations: RESOLVE_RELATIONS,
          per_page: 100,
          sort_by: 'content.date:desc',
          filter_query: {
            __or: ((filters.length && filters) || (categories?.length && categories) || [])
              .map((f) => ({category: {in: f.uuid}}))
              .concat(Array.isArray(authors) ? authors.map((a) => ({author: {in: a}})) : undefined)
              .filter(Boolean),
          },
        })
        setAllStories(data.stories)
        setIsLoading(false)
        setStoryLoaded(true)
      } catch (error) {
        setIsLoading(false)
      }
    }

    fetchStories()
  }, [authors, categories, filters, isSearching])

  React.useEffect(() => {
    if (!storyLoaded || initialFilterDone.current) return
    const filteredStories = allStories.filter((story) =>
      story.name.toLowerCase().includes(currentSearchTerm.current.toLowerCase())
    )
    setStories(filteredStories)

    initialFilterDone.current = true
  }, [storyLoaded, allStories])

  React.useEffect(() => {
    if (isSearching === true) return
    if (filters.length || categories?.length || authors?.length) {
      ;(async function () {
        const {data, headers} = await storyblokApi.get('cdn/stories/', {
          version:
            window.location.pathname.includes('storyblok-preview') || IS_DEVELOPMENT
              ? 'draft'
              : 'published',
          resolve_relations: RESOLVE_RELATIONS,
          per_page: maxItems || 12,
          page,
          sort_by: 'content.date:desc',
          filter_query: {
            __or: ((filters.length && filters) || (categories?.length && categories) || [])
              .map((f) => ({category: {in: f.uuid}}))
              .concat(Array.isArray(authors) ? authors.map((a) => ({author: {in: a}})) : undefined)
              .filter(Boolean),
          },
        })

        setTotal(headers.total)

        setStories((prev) => (page === 1 ? data.stories : prev.concat(data.stories)))
      })()
    } else {
      resetState()
      setStories([])
    }
  }, [page, filters, categories, authors, maxItems, isSearching])

  React.useEffect(() => {
    ScrollTrigger.refresh()
  }, [stories])

  return (
    <section
      {...makeEditable(_editable)}
      className={sectionBackground === 'violet' ? 'bg-violet-600' : backgroundColors.current}
    >
      <Grid className='pb-10'>
        {!!showFilters && (
          <div className='col-span-full mb-4 mt-18 flex flex-wrap gap-4 overflow-x-auto py-8 lg:mb-2 lg:mt-6'>
            {searchPlaceholderLabel && (
              <div className='relative col-span-full flex w-full items-center rounded-2xl border border-purple-300 pl-3 hover:border-violey-700 focus:border-violey-700 active:border-violey-700 md:w-1/2 lg:w-1/3'>
                <input
                  className='misc-button-s w-full bg-transparent px-3 py-4 outline-none placeholder:text-purple-300'
                  type='search'
                  id='search'
                  name='search'
                  value={searchTerm}
                  placeholder={searchPlaceholderLabel}
                  onChange={(e) => {
                    searchInStories(e.target.value)
                    resetState()
                    setSearchTerm(e.target.value)
                  }}
                />

                <span className='relative m-0 block h-fit w-fit p-3 text-purple-300'>
                  <IconSearch />
                </span>
              </div>
            )}

            {categories.map(({uuid, name, content = {}}) => {
              const isActive = filters.find((v) => v.uuid === uuid)

              return (
                <button
                  key={uuid}
                  className={classNames(
                    'misc-button-m rounded-2xl border border-purple-900 px-8 py-4 transition duration-150 hover:bg-purple-900 hover:text-white',
                    {'bg-purple-900 text-white': isActive}
                  )}
                  onClick={() => {
                    resetState()
                    setFilters((prev) => {
                      return isActive ? prev.filter((v) => v.uuid !== uuid) : prev.concat({uuid})
                    })
                  }}
                >
                  {content.title || name}
                </button>
              )
            })}
          </div>
        )}

        <div
          className={classNames('col-span-full', {
            'mt-14': !showFilters,
          })}
        >
          {isLoading && !storyLoaded && (
            <div className='misc-button-m col-span-full text-center '>Chargement...</div>
          )}
          <ul className='grid items-start gap-8 gap-y-10 md:grid-cols-2 md:items-stretch lg:grid-cols-3'>
            {stories.map((story, index) => {
              const hasReachedLimit = !!maxItems && index + 1 > parseInt(maxItems)
              if (!hasReachedLimit) {
                return (
                  <React.Fragment key={story.uuid}>
                    <Card
                      story={story}
                      cardLabel={cardsButtonLabel}
                      index={index}
                      backgroundColor={cardsBackgroundColor}
                    />
                    {components.map((component) => {
                      const isInside =
                        index === 5 || (stories.length < 6 && index === stories.length - 1)

                      if (isInside)
                        return (
                          <div
                            key={component._uid}
                            className='col-span-1 md:col-span-2 lg:col-span-3'
                          >
                            <DynamicComponent {...component} />
                          </div>
                        )
                      return null
                    })}
                  </React.Fragment>
                )
              }

              return null
            })}
          </ul>
        </div>

        {!maxItems && !!total && total > stories.length && (
          <div className='col-span-full mt-26 flex justify-center'>
            <button
              className='misc-button-m rounded-full border border-purple-900 px-8 py-4 text-center transition duration-150 hover:bg-purple-900 hover:text-white'
              onClick={() => setPage((prev) => prev + 1)}
            >
              {showMoreButtonLabel}
            </button>
          </div>
        )}

        {/* eslint-disable-next-line no-warning-comments */}
        {/* https://www.a11ymatters.com/pattern/pagination/ */}
        {filters.length === 0 && !!pagination && (
          <nav
            className='misc-button-m col-span-full mt-12 hidden justify-center gap-3'
            role='navigation'
            aria-label='Navigation de la pagination'
          >
            <ul className='flex justify-center gap-3'>
              {pagination.currentPage !== 1 && (
                <li>
                  <Link
                    to={`${pagination.slug + (pagination.currentPage - 1)}`}
                    aria-label={`Aller à la page suivante, Page ${pagination.currentPage - 1}`}
                    rel='prev'
                  >
                    {'<'}
                  </Link>
                </li>
              )}

              {[...Array(parseInt(pagination.lastPage))].map((_, index) => {
                const {lastPage, currentPage, slug} = pagination
                const offset = 3
                const cursor = index + 1

                if (cursor !== 1 && cursor !== lastPage) {
                  const isFarAway = currentPage - cursor > offset || cursor - currentPage > offset
                  if (isFarAway) return null

                  const isLastBeforeSkipping =
                    currentPage - cursor === offset || cursor - currentPage === offset
                  if (isLastBeforeSkipping) return '…'
                }

                return (
                  <li key={index}>
                    <Link
                      className={classNames('p-1 pl-[0.35rem] text-center', {
                        'rounded-xl bg-purple-200': currentPage === index + 1,
                      })}
                      to={`${slug}${index === 0 ? '' : index + 1}`}
                      aria-label={
                        index + 1 === currentPage
                          ? `Page actuelle, Page ${index + 1}`
                          : `Aller à la page ${index + 1}`
                      }
                      aria-current={index + 1 === currentPage ? 'true' : undefined}
                    >
                      {index + 1}
                    </Link>
                  </li>
                )
              })}

              {pagination.currentPage !== pagination.lastPage && (
                <li>
                  <Link
                    to={`${pagination.slug + (pagination.currentPage + 1)}`}
                    aria-label={`Aller à la page précédente, Page ${pagination.currentPage + 1}`}
                    rel='next'
                  >
                    {'>'}
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        )}
      </Grid>
    </section>
  )
}

export default PagesList
